// @flow
/** @jsx jsx */

import { Colors } from '@zapier/theme';
import { iconsMap } from './icons';
import { marginToCss } from '@zapier/common-utils';
import { css, jsx, keyframes } from '@emotion/core';

import type { Spacing } from '@zapier/common-utils';

export type IconNameType = $Keys<typeof iconsMap>;

type Props = {
  /**
   * Icon fill color - theme.Color, error, success, warning, or string
   */
  color?: string,
  /**
   * If true, spinning animation is added to the icon
   */
  isSpinning?: boolean,
  margin?: Spacing,
  /**
   * Name of icon to display
   */
  name: IconNameType,
  /**
   * Callback to handle click/touch
   */
  onClick?: ?(SyntheticEvent<*>) => void,
  /**
   * Custom Size
   */
  size?: number,
  /**
   * Icon title, used to improve a11y
   */
  title?: string,
};

const defaultProps = {
  isSpinning: false,
  size: 16,
};

const iconColors = {
  error: Colors.status.error.border,
  success: Colors.gamma,
  warning: Colors.wolverine,
};

const spinAnimation = keyframes`
  0% {
    transform: rotate(0);
  }

  100% {
    transform: rotate(-359deg);
  }
`;

const getIconClass = (props: Props) => {
  const base = css({
    ...marginToCss(props.margin),
    display: 'inline-block',
    fill: 'currentColor',
    height: `${props.size || defaultProps.size}px`,
    width: `${props.size || defaultProps.size}px`,
    '> svg': {
      display: 'block',
      height: 'inherit',
      width: 'inherit',
    },
    '*': {
      fill: 'inherit',
    },
  });

  const color =
    props.color &&
    css({
      fill: iconColors[props.color] || Colors[props.color] || props.color,
    });

  const isClickable =
    props.onClick &&
    css({
      cursor: 'pointer',
    });

  const isSpinning =
    props.isSpinning &&
    css({
      animation: `${spinAnimation} 1.5s linear infinite`,
    });

  return css(base, color, isClickable, isSpinning);
};

/**
 * Renders an svg icon based on name
 */
const Icon = (props: Props) => {
  const svgProps = {};
  if (iconsMap[props.name]) {
    // Compatibility with SVG files loaded as strings or components
    if (typeof iconsMap[props.name] === 'string') {
      svgProps.dangerouslySetInnerHTML = {
        __html: iconsMap[props.name],
      };
    } else {
      const IconSvg = iconsMap[props.name];
      svgProps.children = <IconSvg />;
    }
  } else {
    svgProps.children = (
      <svg viewBox="0 0 10 10">
        <rect height="10" width="10" x="0" y="0" />
      </svg>
    );
  }

  /* eslint no-unused-vars: ["error", { "ignoreRestSiblings": true }] */
  /* Omit adding isSpinning attributes to DOM */
  const { isSpinning, title, ...componentProps } = props;

  return (
    <span
      {...componentProps}
      aria-hidden={props.onClick ? undefined : true}
      css={getIconClass(props)}
      title={title}
      {...svgProps}
    />
  );
};

Icon.defaultProps = defaultProps;

export default Icon;
